import { dot, meanVector, subtract } from './linalg';
import type { RandomSource } from './prng';

const MAX_ITERATIONS = 50;

function squaredDistance(a: readonly number[], b: readonly number[]): number {
  const delta = subtract(a, b);
  return dot(delta, delta);
}

function nearestCentroid(
  vector: readonly number[],
  centroids: readonly (readonly number[])[],
): number {
  let best = 0;
  let bestDistance = Infinity;
  for (let c = 0; c < centroids.length; c += 1) {
    const distance = squaredDistance(vector, centroids[c]!);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = c;
    }
  }
  return best;
}

/**
 * k-means over standardised feature vectors — Lloyd's algorithm: assign each
 * point to its nearest centroid, recompute centroids as the mean of their
 * assigned points, repeat until assignments stop changing or the iteration
 * cap is hit.
 *
 * Centroids are seeded from `k` distinct points drawn from the population
 * itself (via `random`, so this is deterministic in the same seed as
 * sampling), not from `k` random vectors in feature space — a random point
 * in a 9-dimensional standardised space is usually nowhere near any real
 * lookalike, and starts converge faster from real ones.
 *
 * A centroid that ends up with no members (empty clusters happen, especially
 * early, with real data) is re-seeded at whichever point is currently
 * furthest from *its own* centroid — the population's least-well-represented
 * point, and a reasonable new seed for an under-served cluster.
 */
export function kMeans(
  vectors: readonly (readonly number[])[],
  k: number,
  random: RandomSource,
): { assignments: number[]; centroids: number[][] } {
  if (vectors.length === 0) return { assignments: [], centroids: [] };
  const clusterCount = Math.min(k, vectors.length);

  const startIndices = new Set<number>();
  while (startIndices.size < clusterCount) {
    startIndices.add(Math.floor(random() * vectors.length));
  }
  let centroids = [...startIndices].map((i) => [...vectors[i]!]);

  let assignments = vectors.map((v) => nearestCentroid(v, centroids));

  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration += 1) {
    const groups: number[][] = Array.from({ length: clusterCount }, () => []);
    assignments.forEach((cluster, index) => {
      groups[cluster]!.push(index);
    });

    const nextCentroids = groups.map((memberIndices) => {
      if (memberIndices.length > 0) {
        return meanVector(memberIndices.map((i) => vectors[i]!));
      }
      // Re-seed an empty cluster at the point furthest from its own
      // centroid — see the doc above.
      let worst = 0;
      let worstDistance = -Infinity;
      assignments.forEach((assignedCluster, index) => {
        const distance = squaredDistance(
          vectors[index]!,
          centroids[assignedCluster]!,
        );
        if (distance > worstDistance) {
          worstDistance = distance;
          worst = index;
        }
      });
      return [...vectors[worst]!];
    });

    const nextAssignments = vectors.map((v) =>
      nearestCentroid(v, nextCentroids),
    );
    const stable = nextAssignments.every(
      (cluster, i) => cluster === assignments[i],
    );
    centroids = nextCentroids;
    assignments = nextAssignments;
    if (stable) break;
  }

  return { assignments, centroids };
}
